import * as React from "react";
import * as _ from "lodash";
import { TreeDataObj } from "../../../models/models";
import { IS_MOBILE, IS_EMBED } from "../../../config";
import { SCREEN_WIDTH } from "../../../constants";
import { IStoreState } from "../../../_reducers";
import { connect } from "react-redux";
import { iNavData } from "../../../models/models";
import { ACTIONS } from "./../../../controllers/App/actions";
import { bindActionCreators, Dispatch } from "redux";

declare var d3;

interface TreeGraphProps {
  graphObj: any;
  currentFilter: string;
  currentSub?: string;
  currentTab?: string;
  data?: any;
  allData?: any;
  navData: iNavData[];
  selectedFilter?: any;
  onSelectedFilter?: (e) => any;
  onSetGraph?: (e) => any;
}

interface TreeGraphState {}

export class TreeGraph extends React.Component<TreeGraphProps, TreeGraphState> {
  private _treeContainer: HTMLDivElement;

  constructor(props: TreeGraphProps) {
    super(props);
  }

  componentWillReceiveProps(props) {
    // Check new data, and data type
    if (
      !props.graphObj ||
      !props.graphObj.data ||
      props.graphObj.data.length === 0 ||
      props.graphObj.graphType !== "graph-categories"
    ) {
      return;
    }

    // Compare datasets & filter
    const currObj = this.props.graphObj;
    if (
      !currObj ||
      !currObj.data ||
      currObj.data.length !== props.graphObj.data.length ||
      this.props.currentFilter !== props.currentFilter
    ) {
      this.drawGraph(props);
    }
  }

  componentDidMount() {
    window.addEventListener("resize", this.didResize);
    this.drawGraph(this.props);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.didResize);
  }

  private didResize = (): void => {
    this.drawGraph(this.props);
  };

  drawGraph = (props: TreeGraphProps): void => {
    if (!props.graphObj || !props.graphObj.data || !props.currentFilter || props.graphObj.graphType !== 'graph-categories') {
      return;
    }

    const margin = {
      top: 2,
      left: 2,
      right: 2,
      bottom: 2
    };

    // Remove the current graph
    d3.select("#tree-graph svg").remove();

    const containerBox = this._treeContainer.getBoundingClientRect();
    const width = containerBox.width;
    const height = containerBox.height;

    // Create the SVG and append to the #tree-graph div
    let svg = d3
      .select("#tree-graph")
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      
    // Create a rectangle that will provide the background color / color between the square tiles
    svg
      .append("rect")
      .attr("width", "100%")
      .attr("height", "100%")


    // Append the 'g' element that will contain the tiles (include the margin to center the element)
    svg = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Treemap requires a specific hierarcy structure of "name" and "children" - Convert the filtered data into this structure
    const filteredData = props.graphObj.data.filter(
      d => d.key === props.currentFilter
    );
    const dataHierarchy = {
      name: props.currentFilter,
      children: filteredData
    };

    // Construct the treemap, telling it to use squares (resquarify) and to use the margin specified above
    const treemap = d3
      .treemap()
      .tile(d3.treemapResquarify)
      .size([
        width - margin.left - margin.right,
        height - margin.top - margin.bottom
      ])
      .round(true)
      .paddingInner(1);

    // Set the data hierachy to the data we converted above.
    // Before applying the treemap layout to our hierarchy we must run .sum() on the hierarchy.
    // This traverses the tree and sets .value on each node to the sum of its children (See https://d3indepth.com/layouts/)
    const root = d3.hierarchy(dataHierarchy).sum(function(d: any) {
      return d.value;
    });

    // Call the treemap layout, passing the hierarchy object as a parameter.
    treemap(root);

    // Position all of the data "leaves" into the treemap...
    var cell = svg
      .selectAll("g")
      .data(root.leaves())
      .enter()
      .append("g")
      .attr("transform", function(d: any) {
        return "translate(" + d.x0 + "," + d.y0 + ")";
      });

    // ...Then add rect elements to these leaves which will be the colored visualizations
    // TODO:: Use the correct colors rather than randomizing them!
    cell
      .append("rect")
      .attr("id", function(d: any) {
        return `rect-${_.kebabCase(d.data.category)}`;
      })
      .attr("width", function(d: any) {
        return d.x1 - d.x0;
      })
      .attr("height", function(d: any) {
        return d.y1 - d.y0;
      })
      .attr("fill", function(d: any) {
        return d.data.color;
        // return `rgb(${Math.ceil(Math.random() * 255)},${Math.ceil(Math.random() * 255)},${Math.ceil(Math.random() * 255)})`;
      })
      .attr("stroke", "#000");

    // Add text for each category onto the graph.
    // TODO:: Reposition / size the text correctly
    cell
      .append("text")
      .attr("id", function(d: any) {
        return `text-${_.kebabCase(d.data.category)}`;
      })
      .attr("class", "square-text-elem")
      .attr("y", function(d, i) {
        const parentBoundingBox = d3
          .select((this as any).parentNode)
          .node()
          .getBBox();
        return parentBoundingBox.height;
      })
      .attr("width", function(d, i) {
        const parentBoundingBox = d3
          .select((this as any).parentNode)
          .node()
          .getBBox();
        return parentBoundingBox.width - 10;
      })
      .attr("x", 5)
      .attr("fill", "#FFF")
      .text(function(d: any) {
        return `${d.data.value
          .toString()
          .replace(
            /(\d)(?=(\d{3})+(?!\d))/g,
            "$1,"
          )} ${d.data.category.trim()}`;
      })
      .call(this.wrap);

    // I'm using this to determine whether or not the text fits the box :)
    cell.each((d: any) => {
      const textElem = d3.select(
        `#text-${_.kebabCase(d.data.category)}`
      ) as any;
      const textElemWidth = textElem.node().getBBox().width;
      const textElemHeight = textElem.node().getBBox().height;

      const rectElem = d3.select(
        `#rect-${_.kebabCase(d.data.category)}`
      ) as any;
      const rectElemWidth = rectElem.node().getBBox().width;
      const rectElemHeight = rectElem.node().getBBox().height;

      // Check current filter - NO CLICKY UNLESS ALL CATEGORIES
      const dataUrl = props.data.url;
      if (props.currentFilter === 'all-tech-giants')
      {
        rectElem.on("mousedown", (d) => {
          this.redirectToTable(_.kebabCase(d.data.category), dataUrl);
        });    
      }

      // Use a spacial padding of 5 (total of 10) to match the tspan x indent / position
      if (
        textElemWidth + 10 > rectElemWidth ||
        textElemHeight + 10 > rectElemHeight
      ) {
        textElem.attr("opacity", 0);

        // Set up hover
        rectElem
          .on("mouseover", cat => {
            const dir = "left";
            this.drawTooltips(cat.data, d3.event.target, dir);
          })
          .on("mouseleave", year => {
            this.removeTooltips();
          });
      }
    });
  }



  redirectToTable = (dataKey: any, url: string): void => {
    // 1. Set graph data - connect redux tree gr
    // 2. Set filter to _.kebabCase(data.category)
    // 3. Redirect (below)
    this.removeTooltips();
    
    const nextGraphData = this.props.allData[`${this.props.currentTab}-categories-overview`];
    
    this.props.onSetGraph(nextGraphData);
    this.props.onSelectedFilter(dataKey);  
    window.location.href = window.location.href.split('#')[0]
                            + (IS_EMBED ? '#/embed' : '#')
                            + url 
                            + '/overview';
  }

  drawTooltips = (
    categoryData: any,
    parentNode: SVGElement,
    direction: "left" | "right"
  ): void => {
    const parentBounds = parentNode.getBoundingClientRect();
    const tipElement = document.createElement("div");

    tipElement.classList.add("tooltip-element-tree");
    tipElement.classList.add(`${direction}-align-tip`);
    tipElement.classList.add("animated");
    tipElement.classList.add("fadeIn");
    tipElement.classList.add("speed-5");


    tipElement.style.position = "absolute";

    // This is how far the user has scrolled down on the page...
    // Add to the tooltip 'top' value to ensure the correct position!
    const windowYOffset = window.pageYOffset || 0;

    // TODO: remove tooltip on scroll :)... Maybe

    if (direction === "left") {
      // change the -100 to a transform: translateX(-100%) :)
      tipElement.style.left = parentBounds.left + "px";
      tipElement.style.transform = "translateX(-100%)";
    } else {
      tipElement.style.left = parentBounds.left + parentBounds.width + "px";
    }

    tipElement.style.top = parentBounds.top + windowYOffset - 15 + "px";
    tipElement.style.width = "150px";
    tipElement.style.height = "auto";
    tipElement.style.border = "2px solid";
    tipElement.style.background = "#FFF";

    // Tooltip Header
    // const ttipHead = document.createElement('h3');
    // ttipHead.innerHTML = yearData[0].year;
    // tipElement.appendChild(ttipHead);

    const ttipCount = document.createElement("h3");
    ttipCount.innerHTML = `${categoryData.value}`.replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      "$1,"
    );
    tipElement.appendChild(ttipCount);

    //Tooltip Header
    const ttipHead = document.createElement('span');
    ttipHead.innerHTML = categoryData.category;
    tipElement.appendChild(ttipHead);

    //Traiangle
    const ttipTriangle = document.createElement('div');
    ttipTriangle.style.borderWidth = "0 0 15px 15px";
    ttipTriangle.style.borderStyle = "solid";
    ttipTriangle.style.borderColor = `transparent transparent ${categoryData.color} transparent`;

    ttipTriangle.style.height = "0";
    ttipTriangle.style.width = "0";
    ttipTriangle.style.position = "absolute";
    ttipTriangle.style.right= "0";
    ttipTriangle.style.bottom= "0";

    ttipTriangle.style.zIndex= "2";
    tipElement.appendChild(ttipTriangle);

    document.body.appendChild(tipElement);
  };

  removeTooltips = (): void => {
    d3.selectAll(".tooltip-element-tree").remove();
    d3.selectAll(".year-dotted-line").attr("opacity", "0");
  };

  wrap = (text): void => {
    text.each(function() {
      var text = d3.select(this);
      var words = text
        .text()
        .split(/\s+/)
        .reverse();
      var lineHeight = 14;
      var width = parseFloat(text.attr("width"));
      var y = parseFloat(text.attr("y"));
      var x = text.attr("x");
      var anchor = text.attr("text-anchor");

      var tspan = text
        .text(null)
        .append("tspan")
        .attr("x", x)
        .attr("y", y)
        .attr("text-anchor", anchor);
      var lineNumber = 0;
      var line = [];
      var word = words.pop();

      // Keep track of the tspans, they will need to be shifted later...
      var tspanArray = [];

      // Value is always on a separate line
      lineNumber += 1;
      tspan.text(word);
      tspan.attr("font-size", SCREEN_WIDTH.IS_LARGE() ? "18px" : "14px");
      tspan.attr("font-family", "Nunito Sans");
      tspan.attr("font-weight", "bold");

      tspanArray.push(tspan);

      tspan = text
        .append("tspan")
        .attr("x", x)
        .attr("y", y + lineNumber * lineHeight)
        .attr("anchor", anchor)
        .text(word);
      tspan.attr("font-size", SCREEN_WIDTH.IS_LARGE() ? "14px" : "12px");
      tspan.attr("font-family", "Nunito Sans");
      // tspan.attr("font-weight", "bold");

      word = words.pop();

      while (word) {
        line.push(word);
        tspan.text(line.join(" "));

        if ((tspan.node() as any).getComputedTextLength() > width) {
          lineNumber += 1;
          line.pop();

          tspan.text(line.join(" "));
          tspan.attr("font-size", SCREEN_WIDTH.IS_LARGE() ? "14px" : "12px");
          tspan.attr("font-family", "Nunito Sans");
          // tspan.attr("font-weight", "bold");

          tspanArray.push(tspan);

          line = [word];
          tspan = text
            .append("tspan")
            .attr("x", x)
            .attr("y", y + lineNumber * lineHeight)
            .attr("anchor", anchor)
            .text(word);
          tspan.attr("font-size", SCREEN_WIDTH.IS_LARGE() ? "14px" : "12px");
          tspan.attr("font-family", "Nunito Sans");
          // tspan.attr("font-weight", "bold");
        }

        word = words.pop();
        if (!word) {
          // Push the final tspan into the array
          tspanArray.push(tspan);
        }
      }

      // Shift the text element up dependant on how mnay lines of text there are
      tspanArray.forEach((tspn, idx) => {
        var currentY = tspn.attr("y");
        var lineDiff = idx === 0 ? 18 : lineHeight;
        tspn.attr("y", currentY - tspanArray.length * lineDiff);
      });
    });
  };

  getFilteredPageData = (): any[] => {
    if (!this.props.data || !this.props.data.data) {
      return [];
    }

    return this.props.data.data.filter(
      graph =>
        !this.props.currentFilter || this.props.currentFilter === graph.key
      // this.props.currentFilter === "all-tech-giants"
    );
  };

  scrollToTop = () => {
    document.getElementsByClassName("app")[0].scrollTop = 0;
  }

  render(): JSX.Element {
    if (!this.props.data) {
      return <p>Loading</p>;
    }

    const filteredData = this.getFilteredPageData();

    return (
      <div
        ref={e => (this._treeContainer = e)}
        className={this.props.selectedFilter === "all-tech-giants"? "tree-graph-container selected-all" : "tree-graph-container"}
      >
        {IS_MOBILE ? (
          <div className="tree-graph-container-mobile">
          <ul>
            <div className="tab-header">
              <div className="tab-header-index">
                <p>RANK</p>
              </div>
              <div className="tab-header-label">
                <p>PATENT CATEGORY</p>
              </div>
              <div className="tab-header-value">
                <p>PATENT COUNT</p>
              </div>
            </div>
            {this.props.data.data &&
              filteredData.map((category, index) => {
                return (
                  <li>
                    <div className="category-index">
                      <p>{index + 1}</p>
                    </div>
                    <div className="category-label">
                      <p>{category.category}</p>
                    </div>
                    <div className="category-value">
                      <p>
                        {`${category.value}`.replace(
                          /(\d)(?=(\d\d\d)+(?!\d))/g,
                          "$1,"
                        )}
                      </p>
                    </div>
                  </li>
                );
              })}
               {/* <span onClick={this.scrollToTop}>Scroll</span> */}
          </ul>
          </div>
        ) : (
          <div id="tree-graph" />
        )}
      </div>
    );
  }
}

const mapStateToProps = (
  state: IStoreState,
  ownProps
): Partial<TreeGraphProps> => {  
  return {
    navData: state.app.navData,
    data: state.app.selectedGraph,
    allData: state.app.data,
    selectedFilter: state.app.selectedFilter
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
      {
        onSetGraph: ACTIONS.SET_GRAPH,
        onSelectedFilter: ACTIONS.SET_FILTER,
      },
      dispatch
    );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TreeGraph);
